import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import {
  getCompanyReportsActivitiesPackagingListQueryKey,
  getCompanyReportsRetrieveQueryKey,
  useCompanyReportsActivitiesDestroy,
  useCompanyReportsActivitiesPackagingList,
  useCompanyReportsActivitiesPackagingPreviewRetrieve,
  useCompanyReportsActivitiesPackagingRetrieve,
  useCompanyReportsActivitiesPackagingUpdate,
  useCompanyReportsPackagingDefaultGroupsList,
} from '@shared/api/lib/company-reports/company-reports';
import {
  CompanyReportsActivitiesPackagingListParams,
  CompanyReportsActivitiesPackagingPreviewRetrieveParams,
  PackagingActivityUpdate,
} from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';

export const useGetPackagingActivityList = (
  companyReportUuid: string,
  params?: CompanyReportsActivitiesPackagingListParams
) => {
  return useCompanyReportsActivitiesPackagingList(companyReportUuid, params, {
    request: useAxiosConfig(),
    query: {
      keepPreviousData: true,
      cacheTime: 1000 * 60 * 60 * 24,
    },
  });
};

export const useGetPackagingActivity = ({
  packagingActivityUuid,
  reportUuid,
}: {
  packagingActivityUuid: string;
  reportUuid: string;
}) => {
  return useCompanyReportsActivitiesPackagingRetrieve(
    reportUuid,
    packagingActivityUuid,
    {
      request: useAxiosConfig(),
    }
  );
};
export const useUpdatePackagingActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesPackagingUpdate<PackagingActivityUpdate>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesPackagingListQueryKey(
            variables.companyReportUuid
          )
        );
      },
    },
  });
};

export const useDeletePackagingActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesPackagingListQueryKey(
            variables.companyReportUuid
          )
        );
      },
    },
  });
};

export const useGetPackagingPreview = ({
  companyReportUuid,
  params,
  enabled = true,
}: {
  companyReportUuid: string;
  params?: CompanyReportsActivitiesPackagingPreviewRetrieveParams;
  enabled?: boolean;
}) => {
  return useCompanyReportsActivitiesPackagingPreviewRetrieve(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        enabled: enabled,
        cacheTime: 0,
        staleTime: 0,
      },
    }
  );
};

export const useGetPackagingCCFDefaults = () => {
  return useCompanyReportsPackagingDefaultGroupsList({
    request: useAxiosConfig(),
  });
};
